<template>
  <div>
    <!-- Measurement Tool Modal -->
    <vue-final-modal
      v-model="measurementModalModel"
      classes="modal-container"
      content-class="modal-content measurement-modal-content"
    >
      <MeasurementTool
        @measurement-confirmed="handleMeasurement"
        @measurement-closed="closeMeasurementModal"
      />
    </vue-final-modal>

    <!-- Information Modal -->
    <vue-final-modal v-model="infoModalModel" classes="modal-container" content-class="modal-content">
      <button class="modal__close" @click="closeModal">
        <i class="far fa-times-circle"></i>
      </button>
      <div class="modal__content">
        <p>{{ information }}</p>
      </div>
    </vue-final-modal>

    <!-- Image Modal -->
    <vue-final-modal v-model="imageModalModel" classes="modal-container" content-class="modal-content">
      <button class="modal__close" @click="closeImageModal">
        <i class="far fa-times-circle"></i>
      </button>
      <div class="modal__content">
        <img v-bind:src="imageBase64" />
      </div>
    </vue-final-modal>

    <!-- Tooth Selector Modal -->
    <vue-final-modal v-model="toothSelectorModalModel" classes="modal-container" content-class="modal-content">
      <div class="modal__content tooth-selector-content">
        <ToothSelector
          :value="selectedToothNumber"
          @input="handleToothSelection"
          @close="closeToothSelectorModal"
          single-select="true"
        />
      </div>
    </vue-final-modal>

    <!-- R-CAT Warning Modal -->
    <vue-final-modal v-model="rcatWarningModalModel" classes="modal-container" content-class="modal-content">
      <button class="modal__close" @click="cancelRCatSelection">
        <i class="far fa-times-circle"></i>
      </button>
      <div class="modal__content">
        <div class="rcat-warning-content">
          <h3>Warning</h3>
          <p>It may be impractical/unfeasible to treat this case. Do you want to continue with the assessment?</p>
          <div class="warning-actions">
            <button class="btn-secondary" @click="cancelHighRiskSelection">
              <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
              No, Go to Summary
            </button>
            <button class="btn-primary" @click="confirmRCatSelection">
              <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
              Yes, Continue
            </button>
          </div>
        </div>
      </div>
    </vue-final-modal>
  </div>
</template>

<script>
import MeasurementTool from '../MeasurementTool.vue'
import ToothSelector from '../ToothSelector.vue';

export default {
  name: 'SurveyModals',
  
  components: {
    MeasurementTool,
    ToothSelector,
  },

  props: {
    showMeasurementModal: {
      type: Boolean,
      default: false
    },
    showModal: {
      type: Boolean,
      default: false
    },
    showImageModal: {
      type: Boolean,
      default: false
    },
    showToothSelectorModal: {
      type: Boolean,
      default: false
    },
    showRCatWarningModal: {
      type: Boolean,
      default: false
    },
    information: {
      type: String,
      default: ""
    },
    imageBase64: {
      type: String,
      default: ""
    },
    selectedToothNumber: {
      type: String,
      default: ""
    }
  },

  computed: {
    measurementModalModel: {
      get() {
        return this.showMeasurementModal;
      },
      set(value) {
        this.$emit('update:showMeasurementModal', value);
      }
    },
    
    infoModalModel: {
      get() {
        return this.showModal;
      },
      set(value) {
        this.$emit('update:showModal', value);
      }
    },
    
    imageModalModel: {
      get() {
        return this.showImageModal;
      },
      set(value) {
        this.$emit('update:showImageModal', value);
      }
    },
    
    toothSelectorModalModel: {
      get() {
        return this.showToothSelectorModal;
      },
      set(value) {
        this.$emit('update:showToothSelectorModal', value);
      }
    },
    
    rcatWarningModalModel: {
      get() {
        return this.showRCatWarningModal;
      },
      set(value) {
        this.$emit('update:showRCatWarningModal', value);
      }
    }
  },

  methods: {
    handleMeasurement(data) {
      this.$emit('measurement-confirmed', data);
    },

    closeMeasurementModal() {
      this.$emit('update:showMeasurementModal', false);
    },

    closeModal() {
      this.$emit('update:showModal', false);
    },

    closeImageModal() {
      this.$emit('update:showImageModal', false);
    },

    handleToothSelection(toothNumber) {
      this.$emit('tooth-selected', toothNumber);
    },

    closeToothSelectorModal() {
      this.$emit('update:showToothSelectorModal', false);
    },

    confirmRCatSelection() {
      this.$emit('rcat-confirmed');
    },

    cancelRCatSelection() {
      this.$emit('rcat-cancelled');
    },

    cancelHighRiskSelection() {
      this.$emit('high-risk-cancelled');
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../../breakpoints.scss";

// Modal Styles
::v-deep .modal-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep .modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0 1rem;
  padding: 24px;
  border: 1px solid #E5E7EB;
  border-radius: 10px;
  background: white;
  width: 90%;
  max-width: 800px;
  overflow-y: auto;
  max-height: 90vh;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  img {
    width: 100%;
    max-width: 100%;
    height: auto;
    border-radius: 4px;

    @include md {
      width: auto;
      max-width: 100%;
      max-height: 70vh;
    }
  }

  p {
    line-height: 1.6;
    color: #000001;
    font-size: 16px;
  }
}

.modal__close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: 0;
  font-size: 32px;
  color: #6B7280;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #111827;
  }
}

.rcat-warning-content {
  text-align: center;

  h3 {
    color: #111827;
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 16px 0;
  }

  p {
    margin: 0 0 24px 0;
    font-size: 16px;
    color: #374151;
    line-height: 1.5;
  }

  .warning-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;

    @include md {
      flex-direction: row;
      justify-content: center;
      gap: 16px;
    }
  }
}

.modal__content {
  margin-top: 10px;
  display: flex;
  justify-content: center;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
  width: 100%;

  @include md {
    width: auto;
  }

  .btn-icon {
    flex-shrink: 0;
  }
}

.btn-primary {
  background: linear-gradient(135deg, #013B82, #0A2D5C);
  color: white;
  box-shadow: 0 2px 6px rgba(1, 59, 130, 0.2);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(1, 59, 130, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(1, 59, 130, 0.2);
  }
}

.btn-secondary {
  background: white;
  color: #000001;
  border: 1px solid #D1D5DB;

  &:hover {
    background: #F9FAFB;
    color: #111827;
    border-color: #9CA3AF;
  }

  &:active {
    background: #F3F4F6;
  }
}
</style>